import { useEffect, useState, type FunctionComponent } from 'react';
import { useFlags, withLDConsumer, type LDClient } from 'launchdarkly-react-client-sdk';
import { useLocation } from 'react-router-dom';
import { trackPageLoad } from '@fff-web/fff-analytics';
import { useUserContext } from '@fff-web/fff-utilities';
import AllErrorMessage from '../../components/AllErrorMessage';
import Layout from '../../components/Layout';
import { searchToObject } from '../../helpers';
import useLaunchDarkly from '../../hooks/useLaunchDarkly';
import { redirectToBaseSite } from '../../utils/redirectMethods';
import MainBanner from './MainBanner';
import PerksBanner from './PerksBanner';
import ReactivateTodayDisclaimer from './ReactivateTodayDisclaimer';
import * as styles from './ReactivateToday.module.scss';

type ReactivateTodayProps = {
  ldClient: LDClient;
  location: { search: string };
};

export const ReactivateToday: FunctionComponent<ReactivateTodayProps> = ({ ldClient }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showRetry, setShowRetry] = useState(false);
  const { userInfo } = useUserContext();
  useLaunchDarkly(ldClient, userInfo);
  const { chatbotAppliedLab: showChatbot } = useFlags();
  const location = useLocation();
  const { coupon_code: couponCode } = searchToObject(location.search);

  const isCanceled = userInfo && userInfo.user && userInfo.user.subscriptionState === 'CANCELED';

  useEffect(() => {
    if (isCanceled) {
      trackPageLoad({
        properties: { coupon_code: couponCode },
      }).then();
    }
  }, []);

  if (!isCanceled) {
    redirectToBaseSite();
    return null;
  }

  return (
    <Layout
      isLoading={isLoading}
      pageTitle="Reactivate Today"
      showError={showRetry}
      ErrorComponent={() => (
        <AllErrorMessage subjectAction="reactivate" onButtonClick={() => setShowRetry(false)} />
      )}
      hideChatWidget={!showChatbot}
    >
      <MainBanner isLoading={isLoading} setIsLoading={setIsLoading} setShowRetry={setShowRetry} />
      <div className={styles.divider} data-testid="reactivate-today-divider" />
      <PerksBanner />
      <ReactivateTodayDisclaimer
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        setShowRetry={setShowRetry}
      />
    </Layout>
  );
};

export default withLDConsumer()(ReactivateToday);
